/**
 * Product Visibility Service
 *
 * Story 56: Track Product SEO Visibility in Google Search
 */

import { authFetch } from '@/utils/auth-fetch';

export interface VisibilitySearchResult {
  query: string;
  position: number | null;
  found: boolean;
  matchedUrl?: string | null;
  error?: string;
}

export interface ResearchTermResult {
  term: string;
  position: number | null;
  found: boolean;
  matchedUrl?: string | null;
  error?: string;
}

export interface QuestionSearchResult extends VisibilitySearchResult {
  hasFeaturedSnippet?: boolean;
  featuredSnippetHolder?: string | null;
}

export interface VisibilityReport {
  productId: string;
  productName: string;
  productUrl: string;
  market: string;
  marketName: string;
  brandSearch: VisibilitySearchResult;
  researchTerms: ResearchTermResult[];
  questionSearch: QuestionSearchResult | null;
  trackingEnabled: boolean;
  lastChecked: string;
}

export interface TrackingResult {
  trackingId: string;
  keywordsCreated: number;
  estimatedDailyCost: number;
  researchTerms: string[];
  question: string | null;
}

export interface TrackingStatus {
  tracking: boolean;
  lastChecked?: string;
  researchTerms?: string[];
  question?: string;
}

export interface DashboardProduct {
  tracking_id: string;
  product_id: string;
  product_name: string;
  tracking_enabled: boolean;
  last_visibility_check: string | null;
  research_terms: string[];
  question: string | null;
  brand_position: number | null;
  brand_previous_position: number | null;
  positionChange: number | null;
}

export interface DashboardSummary {
  totalTracked: number;
  checkedToday: number;
  avgPosition: string | null;
  top10Count: number;
  notRankingCount: number;
}

export interface VisibilityAlert {
  id: string;
  product_visibility_id: string;
  site_id: string;
  search_type: string;
  search_query: string;
  previous_position: number | null;
  new_position: number | null;
  position_change: number;
  alert_type: 'improvement' | 'decline';
  is_read: boolean;
  created_at: string;
  product_name?: string;
}

export interface DashboardData {
  summary: DashboardSummary;
  products: DashboardProduct[];
  alerts: VisibilityAlert[];
  unreadAlertCount: number;
}

export interface PositionHistoryEntry {
  date: string;
  brand: number | null;
  research: (number | null)[];
  researchAvg: number | null;
  question: number | null;
}

export interface PositionHistory {
  history: PositionHistoryEntry[];
}

/**
 * Check visibility for a product
 */
export async function checkProductVisibility(
  productId: string,
  siteId: string,
  forceRefresh = false,
  market = 'US'
): Promise<VisibilityReport> {
  const params = new URLSearchParams({ siteId, market });
  if (forceRefresh) params.append('forceRefresh', 'true');

  const response = await authFetch(`/api/products/${productId}/visibility?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check visibility');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get tracking status for a product
 */
export async function getTrackingStatus(productId: string): Promise<TrackingStatus> {
  const response = await authFetch(`/api/products/${productId}/visibility/status`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get tracking status');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Enable tracking for a product
 */
export async function enableProductTracking(
  productId: string,
  siteId: string
): Promise<TrackingResult> {
  const response = await authFetch(`/api/products/${productId}/visibility/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to enable tracking');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Disable tracking for a product
 */
export async function disableProductTracking(productId: string): Promise<void> {
  const response = await authFetch(`/api/products/${productId}/visibility/track`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to disable tracking');
  }
}

/**
 * Get position history for a product
 */
export async function getPositionHistory(
  productId: string,
  days = 30
): Promise<PositionHistory> {
  const response = await authFetch(`/api/products/${productId}/visibility/history?days=${days}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get position history');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get dashboard data for a site
 */
export async function getDashboardData(
  siteId: string,
  days = 30
): Promise<DashboardData> {
  const response = await authFetch(`/api/sites/${siteId}/visibility/dashboard?days=${days}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get dashboard data');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get tracked products for a site
 */
export async function getTrackedProducts(siteId: string): Promise<{
  products: DashboardProduct[];
  totalTracked: number;
}> {
  const response = await authFetch(`/api/sites/${siteId}/visibility/products`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get tracked products');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Mark alerts as read
 */
export async function markAlertsAsRead(alertIds: string[]): Promise<void> {
  const response = await authFetch('/api/visibility/alerts/read', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ alertIds })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to mark alerts as read');
  }
}
